import axios from 'axios';
import firebase from 'firebase';
import dayjs from 'dayjs';
import { notification } from 'antd';

const showNotification = (type, title, description) => {
  notification[type]({
    message: title,
    description,
  });
};

/** Deployed API Gateway (v2). */
export const MORPHEUS_GATEWAY = 'https://morpheus-gateway-apa76zgs.ts.gateway.dev';

/**
 * Optional base URL override for local testing only.
 * Example: REACT_APP_MORPHEUS_URL=http://127.0.0.1:8080
 */
function explicitMorpheusUrl() {
  const u = process.env.REACT_APP_MORPHEUS_URL;
  return u && String(u).trim() ? String(u).trim().replace(/\/$/, '') : null;
}

function resolveInitialBaseUrl() {
  return explicitMorpheusUrl() || MORPHEUS_GATEWAY;
}

const client = axios.create({
  baseURL: resolveInitialBaseUrl(),
  json: true,
  headers: {
    'X-Api-Key': 'AIzaSyDRJEGhXFnZeV2zu1aUXm2Soc-JmAR358U',
  },
});

// Intercept outgoing requests to include auth token and decamelize
client.interceptors.request.use(
  async (config) => {
    const newConfig = { ...config };
    if (firebase.auth().currentUser) {
      newConfig.headers = {
        ...newConfig.headers,
        'X-Auth-Token': await firebase.auth().currentUser.getIdToken(true),
      };
    }
    return newConfig;
  },
  (error) => Promise.reject(error),
);

// Get all briefs
export const getBriefs = () => client({
  method: 'GET',
  url: '/products/briefs',
})
  .then((response) => response.data)
  .catch(() => {
    showNotification('error', 'Error loading briefs');
  });

// Create a new brief
export const createBrief = ({ data }) => client({
  method: 'POST',
  url: '/products/briefs',
  data,
}).then((response) => response.data);

// Get a brief by ID
export const getBrief = (id) => client({
  method: 'GET',
  url: `/products/briefs/${id}`,
})
  .then(({ data: brief }) => brief)
  .catch(() => {
    showNotification('error', 'Error loading brief');
  });

// Update an existing brief
export const updateBrief = ({
  id,
  data,
  sessionId = null,
}) => client({
  method: 'PUT',
  url: `/products/briefs/${id}`,
  params: {
    sessionId,
  },
  data,
})
  .then((response) => response.data)
  .catch((error) => Promise.reject(error));

// Delete a brief
export const deleteBrief = (id) => client({
  method: 'DELETE',
  url: `/products/briefs/${id}`,
}).then(() => {
  showNotification('success', 'Brief deleted!');
}).catch(() => {
  showNotification('error', 'Error deleting brief');
});

/**
 * Max labels per API call — keeps each response under gateway / proxy size limits.
 * Multiple calls are merged client-side (no Morpheus deploy required).
 * Keep conservative: heavy SKUs can still produce multi‑MB JSON per chunk at 200.
 */
const MAX_LABELS_PER_REQUEST = 100;

/** Chunked base64 for large PDF ArrayBuffers (avoids call stack limits). */
function arrayBufferToBase64(buffer) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

function base64PdfToUint8Array(base64) {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Single request; returns base64 PDF string (same format as before). */
function requestPackingLabelsOnce({
  type, productId, product, formData,
}) {
  return client({
    method: 'POST',
    url: `/products/${productId}/packing-labels`,
    params: { type },
    data: {
      product,
      ...formData,
      date: dayjs(formData.date).startOf('day').format(), // Convert from dayjs to string
    },
    responseType: 'arraybuffer',
    validateStatus: () => true,
  }).then((response) => {
    const ct = response.headers['content-type'] || '';

    if (response.status >= 400) {
      const text = new TextDecoder().decode(new Uint8Array(response.data));
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        payload = { error: { message: text || `HTTP ${response.status}` } };
      }
      const err = new Error(payload.error?.message || 'Error generating labels');
      err.response = { data: payload, status: response.status };
      throw err;
    }

    if (ct.includes('application/pdf')) {
      return arrayBufferToBase64(response.data);
    }

    const text = new TextDecoder().decode(new Uint8Array(response.data));
    const data = JSON.parse(text);
    const { pdf } = data;
    return pdf;
  });
}

/** One retry on transport failures (no HTTP response), e.g. gateway closing large streams. */
async function requestPackingLabelsOnceWithRetry(params) {
  try {
    return await requestPackingLabelsOnce(params);
  } catch (err) {
    const noHttpResponse = !err.response;
    const looksLikeTransport = noHttpResponse && (
      err.message === 'Network Error'
      || err.code === 'ERR_NETWORK'
      || err.code === 'ECONNABORTED'
    );
    if (!looksLikeTransport) throw err;
    return requestPackingLabelsOnce(params);
  }
}

// Generate packing labels for a product
export const generatePackingLabels = async ({
  type, productId, product, formData,
}) => {
  const palletQtyBlank = type === 'pallet'
    && (formData.quantity == null || formData.quantity === '');
  const quantity = palletQtyBlank ? null : (Number(formData.quantity) || 1);
  const normalizedForm = { ...formData, quantity };

  try {
    if (type === 'pallet' || quantity <= MAX_LABELS_PER_REQUEST) {
      return await requestPackingLabelsOnceWithRetry({
        type, productId, product, formData: normalizedForm,
      });
    }

    const { PDFDocument } = await import('pdf-lib');
    const merged = await PDFDocument.create();
    let remaining = quantity;

    /* Sequential chunk requests — must await each before the next. */
    /* eslint-disable no-await-in-loop */
    while (remaining > 0) {
      const chunk = Math.min(remaining, MAX_LABELS_PER_REQUEST);
      const base64 = await requestPackingLabelsOnceWithRetry({
        type,
        productId,
        product,
        formData: { ...normalizedForm, quantity: chunk },
      });
      const src = await PDFDocument.load(base64PdfToUint8Array(base64));
      const copied = await merged.copyPages(src, src.getPageIndices());
      copied.forEach((page) => merged.addPage(page));
      remaining -= chunk;
    }
    /* eslint-enable no-await-in-loop */

    const outBytes = await merged.save();
    return arrayBufferToBase64(outBytes);
  } catch (error) {
    showNotification('error', 'Error generating labels');
    throw error;
  }
};
